import { Component, Inject, OnDestroy, OnInit, ElementRef, Renderer2 } from "@angular/core";
import { DOCUMENT } from "@angular/common";
import { Title } from "@angular/platform-browser";

import { Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";
import { TranslateService } from "@ngx-translate/core";
import * as Waves from "node-waves";

import { CoreMenuService } from "@core/components/core-menu/core-menu.service";
import { CoreSidebarService } from "@core/components/core-sidebar/core-sidebar.service";
import { CoreConfigService } from "@core/services/config.service";
import { CoreLoadingScreenService } from "@core/services/loading-screen.service";
import { CoreTranslationService } from "@core/services/translation.service";

import { menu } from "app/menu/menu";
import { locale as menuEnglish } from "app/menu/i18n/en";
import { locale as menuFrench } from "app/menu/i18n/fr";
import { locale as menuGerman } from "app/menu/i18n/de";
import { locale as menuPortuguese } from "app/menu/i18n/pt";
import { FirebaseMessagingService } from "./services/firebase-messaging.service";
import { environment } from "../environments/environment";
import { CurrentService } from "./services/current.service";
import { RoleService } from "./services/role.service";

@Component({
  selector: "app-root",
  templateUrl: "./app.component.html",
  styleUrls: ["./app.component.scss"],
})
export class AppComponent implements OnInit, OnDestroy {
  coreConfig: any;
  menu: any;
  defaultLanguage: "fr";
  appLanguage: "fr";

  private _unsubscribeAll: Subject<any>;

  constructor(
    @Inject(DOCUMENT) private document: any,
    private _title: Title,
    private _renderer: Renderer2,
    private _elementRef: ElementRef,
    public _coreConfigService: CoreConfigService,
    private _coreSidebarService: CoreSidebarService,
    private _coreMenuService: CoreMenuService,
    private _coreTranslationService: CoreTranslationService,
    private _translateService: TranslateService,
    private _coreLoadingScreenService: CoreLoadingScreenService,
    private _firebaseMessaging: FirebaseMessagingService,
    private _currentService: CurrentService,
    private _roleService: RoleService
  ) {
    this.menu = menu;
    this._coreMenuService.register("main", this.menu);
    this._coreMenuService.setCurrentMenu("main");
    this._translateService.addLangs(["en", "fr", "de", "pt"]);
    this._translateService.setDefaultLang("en");
    this._coreTranslationService.translate(menuEnglish, menuFrench, menuGerman, menuPortuguese);
    this._unsubscribeAll = new Subject();
  }

  ngOnInit(): void {
    // Charger les permissions si l'utilisateur est connecté
    this.loadPermissionsIfLoggedIn();

    // Logs de configuration
    console.log("🚀 ════════════════════════════════════════");
    console.log("💻 BACK OFFICE - Configuration");
    console.log("🚀 ════════════════════════════════════════");
    console.log("Environment:", environment.production ? "PRODUCTION" : "DEVELOPMENT");
    console.log("API URL:", environment.apiUrl);
    console.log("");
    console.log("🔥 ════════════════════════════════════════");
    console.log("🔥 FIREBASE Configuration");
    console.log("🔥 ════════════════════════════════════════");
    console.log("Project ID:", environment.firebase.projectId);
    console.log("Messaging Sender ID:", environment.firebase.messagingSenderId);
    console.log("App ID:", environment.firebase.appId);
    console.log("API Key:", environment.firebase.apiKey);
    console.log("VAPID Key:", environment.vapikey);
    console.log("🔥 ════════════════════════════════════════");
    console.log("");

    // Log du statut des notifications
    console.log("🔔 ════════════════════════════════════════");
    console.log("🔔 NOTIFICATIONS Status");
    console.log("🔔 ════════════════════════════════════════");
    if ("Notification" in window) {
      console.log("Notification API: ✅ Disponible");
      console.log("Permission actuelle:", Notification.permission);
      console.log(
        "Notifications autorisées:",
        Notification.permission === "granted" ? "✅ OUI" : "❌ NON"
      );
    } else {
      console.log("Notification API: ❌ Non supportée par le navigateur");
    }
    console.log("🔔 ════════════════════════════════════════");
    console.log("");

    Waves.init();

    // Initialize Firebase Push Notifications
    this.initializeFirebaseMessaging();

    this._coreConfigService.config.pipe(takeUntil(this._unsubscribeAll)).subscribe((config) => {
      this.coreConfig = config;
      const appLanguage = this.coreConfig.app.appLanguage || "fr";
      this._translateService.use(appLanguage);

      setTimeout(() => {
        this._translateService.setDefaultLang("fr");
        this._translateService.setDefaultLang(appLanguage);
      });

      this._elementRef.nativeElement.classList.remove(
        "vertical-layout",
        "vertical-menu-modern",
        "horizontal-layout",
        "horizontal-menu"
      );
      if (this.coreConfig.layout.type === "vertical") {
        this._elementRef.nativeElement.classList.add("vertical-layout", "vertical-menu-modern");
      } else if (this.coreConfig.layout.type === "horizontal") {
        this._elementRef.nativeElement.classList.add("horizontal-layout", "horizontal-menu");
      }

      this._elementRef.nativeElement.classList.remove(
        "navbar-floating",
        "navbar-static",
        "navbar-sticky",
        "navbar-hidden"
      );
      if (this.coreConfig.layout.navbar.type === "navbar-static-top") {
        this._elementRef.nativeElement.classList.add("navbar-static");
      } else if (this.coreConfig.layout.navbar.type === "fixed-top") {
        this._elementRef.nativeElement.classList.add("navbar-sticky");
      } else if (this.coreConfig.layout.navbar.type === "floating-nav") {
        this._elementRef.nativeElement.classList.add("navbar-floating");
      } else {
        this._elementRef.nativeElement.classList.add("navbar-hidden");
      }

      this._elementRef.nativeElement.classList.remove(
        "footer-fixed",
        "footer-static",
        "footer-hidden"
      );
      if (this.coreConfig.layout.footer.type === "footer-sticky") {
        this._elementRef.nativeElement.classList.add("footer-fixed");
      } else if (this.coreConfig.layout.footer.type === "footer-static") {
        this._elementRef.nativeElement.classList.add("footer-static");
      } else {
        this._elementRef.nativeElement.classList.add("footer-hidden");
      }

      if (
        this.coreConfig.layout.menu.hidden &&
        this.coreConfig.layout.navbar.hidden &&
        this.coreConfig.layout.footer.hidden
      ) {
        this._elementRef.nativeElement.classList.add("blank-page");
        const appContentElement =
          this._elementRef.nativeElement.getElementsByClassName("app-content")[0];
        if (appContentElement) {
          this._renderer.setAttribute(appContentElement, "style", "transition:none");
        }
      } else {
        this._elementRef.nativeElement.classList.remove("blank-page");
        setTimeout(() => {
          const appContentElement =
            this._elementRef.nativeElement.getElementsByClassName("app-content")[0];
          if (appContentElement) {
            this._renderer.setAttribute(appContentElement, "style", "transition:300ms ease all");
          }
        }, 0);
        if (this.coreConfig.layout.navbar.hidden) {
          this._elementRef.nativeElement.classList.add("navbar-hidden");
        }
        if (this.coreConfig.layout.menu.hidden) {
          this._renderer.setAttribute(this._elementRef.nativeElement, "data-col", "1-column");
        } else {
          this._renderer.removeAttribute(this._elementRef.nativeElement, "data-col");
        }
        if (this.coreConfig.layout.footer.hidden) {
          this._elementRef.nativeElement.classList.add("footer-hidden");
        }
      }
      if (this.coreConfig.layout.skin !== "" && this.coreConfig.layout.skin !== undefined) {
        this.document.body.classList.remove(
          "default-layout",
          "bordered-layout",
          "dark-layout",
          "semi-dark-layout"
        );
        this.document.body.classList.add(this.coreConfig.layout.skin + "-layout");
      }
    });
    this._title.setTitle(this.coreConfig.app.appTitle);
  }

  private async initializeFirebaseMessaging(): Promise<void> {
    try {
      const currentUser = localStorage.getItem("currentUser");
      if (currentUser) {
        console.log("🔔 Requesting notification permission...");
        await this._firebaseMessaging.requestPermission();
        this._firebaseMessaging.listenToMessages();
      }
    } catch (error) {
      console.error("❌ Error initializing Firebase messaging:", error);
    }
  }

  /**
   * Charge les permissions si l'utilisateur est déjà connecté
   * Appelé au démarrage de l'application pour les sessions existantes
   */
  private loadPermissionsIfLoggedIn(): void {
    const currentUser = this._currentService.currentUser.value;
    const currentTenant = this._currentService.currentTenant.value;

    // Si un utilisateur est connecté et qu'un tenant est sélectionné
    if (currentUser?.refreshToken && currentTenant?.id) {
      // Vérifier si les permissions sont déjà chargées depuis le localStorage
      if (!this._roleService.arePermissionsLoaded()) {
        console.log("🔐 Chargement des permissions au démarrage...");
        this._roleService.loadUserPermissions().subscribe({
          next: (permissions) => {
            console.log(
              `✅ Permissions chargées au démarrage: ${
                permissions.permissions?.length || 0
              } permissions`
            );
          },
          error: (err) => {
            console.warn("⚠️ Impossible de charger les permissions au démarrage:", err);
          },
        });
      } else {
        console.log(
          `🔐 Permissions déjà en cache: ${
            this._roleService.getCurrentPermissions().length
          } permissions`
        );
      }
    }
  }

  ngOnDestroy(): void {
    this._unsubscribeAll.next();
    this._unsubscribeAll.complete();
  }

  toggleSidebar(key): void {
    this._coreSidebarService.getSidebarRegistry(key).toggleOpen();
  }
}
